import React from "react";

/**
 * Template #6 — label_anchor (3D floating label)
 * Explain terminology clearly. Short text, never paragraphs.
 * Parts: label
 * Actions: draw, attach, fade
 *
 * 3D floating pill label with connecting line, shadow, and depth.
 */

export interface LabelAnchorProps {
  anchorX?: number;
  anchorY?: number;
  text?: string;
  position?: "top" | "bottom" | "left" | "right";
  showLine?: boolean;
  opacity?: number;
  accentColor?: string;
}

export const LabelAnchor: React.FC<LabelAnchorProps> = ({
  anchorX = 400,
  anchorY = 200,
  text = "Label",
  position = "top",
  showLine = true,
  opacity = 1,
  accentColor = "#a78bfa",
}) => {
  const lineLen = 44;
  const pad = { x: 14, y: 8 };

  const offsets: Record<string, { x: number; y: number }> = {
    top: { x: 0, y: -lineLen },
    bottom: { x: 0, y: lineLen },
    left: { x: -lineLen - 40, y: 0 },
    right: { x: lineLen + 40, y: 0 },
  };

  const lineEnds: Record<string, { x: number; y: number }> = {
    top: { x: anchorX, y: anchorY - lineLen + 12 },
    bottom: { x: anchorX, y: anchorY + lineLen - 12 },
    left: { x: anchorX - lineLen + 12, y: anchorY },
    right: { x: anchorX + lineLen - 12, y: anchorY },
  };

  const off = offsets[position];
  const le = lineEnds[position];
  const lx = anchorX + off.x;
  const ly = anchorY + off.y;
  const tw = text.length * 7.5 + pad.x * 2;
  const th = 26;

  return (
    <g opacity={opacity}>
      {/* Connecting line */}
      {showLine && (
        <g>
          {/* Shadow line */}
          <line x1={anchorX + 1} y1={anchorY + 1} x2={le.x + 1} y2={le.y + 1} stroke="#000" strokeWidth={2} opacity={0.15} />
          <line x1={anchorX} y1={anchorY} x2={le.x} y2={le.y} stroke={accentColor} strokeWidth={1.5} opacity={0.6} />
          {/* Anchor dot — 3D sphere */}
          <circle cx={anchorX} cy={anchorY} r={4} fill="#0f172a" stroke={accentColor} strokeWidth={1.5} />
          <circle cx={anchorX - 1} cy={anchorY - 1} r={1.2} fill="#fff" opacity={0.25} />
        </g>
      )}

      {/* Label pill shadow */}
      <rect x={lx - tw / 2 + 2} y={ly - th / 2 + 2} width={tw} height={th} rx={th / 2} fill="#000" opacity={0.2} />

      {/* Label pill background */}
      <rect x={lx - tw / 2} y={ly - th / 2} width={tw} height={th} rx={th / 2} fill="#1e293b" stroke={accentColor} strokeWidth={1.2} />
      {/* Top specular line */}
      <rect x={lx - tw / 2 + 6} y={ly - th / 2 + 1} width={tw - 12} height={3} rx={1.5} fill="#fff" opacity={0.04} />

      {/* Label text */}
      <text
        x={lx}
        y={ly + 1}
        fill="#e2e8f0"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={12}
        fontWeight={500}
        fontFamily="'Inter', system-ui, sans-serif"
      >
        {text}
      </text>
    </g>
  );
};
